import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  return String(value);
};

const resolveCurrentDateOrToday = (value: unknown): string => {
  if (value != null && typeof value === "object") {
    const row = value as Record<string, any>;
    const meta = row.metadata ?? row.meta ?? row.metaData ?? {};
    return formatEpochDate(
      row.currentDate ?? row.current_date ?? meta.currentDate ?? Date.now(),
    );
  }
  return formatEpochDate(value ?? Date.now());
};

const getHoldingSchemeName = (row: Record<string, any>): string =>
  String(row.symbol ?? row.schemeName ?? row.name ?? "-");

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ?? row.txnType ?? row.transactionType ?? row.transactiontype ?? "",
  ).trim();
  if (!raw) return false;
  return isEquitySellType(raw) || raw.toLowerCase().includes("sell");
};

const toNum = (v: any): number | null => {
  if (v === null || v === undefined || v === "" || v === "-") return null;
  const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((row) => {
    if (!row || typeof row !== "object") return true;
    const units = toNum(row.units ?? row.quantity ?? row.qty);
    if (units === null) return true;
    if (units > 0) return true;
    const invested = toNum(row.investedAmount ?? row.costValue ?? row.amount);
    const current = toNum(row.currentAmount ?? row.currentValue ?? row.marketValue);
    return (invested !== null && invested > 0) || (current !== null && current > 0);
  });

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    meta.averageDays ??
    meta.days;
  return val != null && val !== "" ? val : "-";
};

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrPrivateStock"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      purchaseDate: formatEpochDate(row.purchaseDate ?? row.purchase_date),
      currentDate: resolveCurrentDateOrToday(row),
      units: row.units ?? row.quantity,
      amount: row.amount ?? row.investedAmount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue ?? row.marketValue,
      averagePrice:
        row.averagePrice ?? row.avgPrice ?? row.averageNav ?? meta.averagePrice,
      currentPrice:
        row.currentPrice ?? row.currentNAV ?? row.currentNav ?? meta.currentPrice,
      gainLoss:
        row.gainLoss ??
        row.unrealisedPNL ??
        row.unrealizedPNL ??
        row.unrealisedGainLoss,
      gainLossPerc:
        row.gainLossPerc ??
        row.gainLossPercentage ??
        row.unrealisedPNLPercentage ??
        row.unrealizedPNLPercentage,
      days: resolveHoldingAverageDays(row),
      cagr: resolveHoldingCagr(row),
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.meta ?? {};
  const schemeName = getHoldingSchemeName(row);
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.avgPrice ??
    row.averagePrice ??
    meta.avgBuyPrice ??
    meta.averagePrice;
  const sellPrice =
    row.currentPrice ?? row.currentNAV ?? row.purchaseNAV ?? row.purchasePrice;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName,
      symbol: row.symbol ?? schemeName,
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      purchaseDate: formatEpochDate(row.purchaseDate ?? row.purchase_date),
      currentDate: resolveCurrentDateOrToday(row),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      purchaseNAV: sellPrice ?? row.purchaseNAV ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: row.saleValue ?? row.amount ?? row.currentAmount,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPerc ??
        row.gainLossPercentage,
      cagr: resolveHoldingCagr(row),
    }),
  );
};

const flattenPrivateEquityHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = getHoldingSchemeName(h);
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    for (const t of list) {
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...t,
            schemeName: t.schemeName ?? schemeName,
            symbol: t.symbol ?? h.symbol ?? schemeName,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date,
            ),
            purchaseDate: formatEpochDate(
              t.purchaseDate ?? t.purchase_date ?? h.purchaseDate,
            ),
            currentDate: resolveCurrentDateOrToday({
              ...h,
              ...t,
              currentDate: t.currentDate ?? h.currentDate,
            }),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            purchasePrice:
              t.purchasePrice ?? t.price ?? t.avgPrice ?? t.averagePrice,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue ?? h.currentAmount,
            units: t.units ?? t.quantity,
            days: t.days ?? t.avgDays ?? h.averageDays,
            cagr: t.cagr ?? h.cagr ?? resolveHoldingCagr(h),
            realisedPnl:
              t.realisedPnl ??
              t.realisedPnL ??
              t.realisedPNL ??
              t.realizedPnl ??
              t.realizedPNL,
            realisedPNLPercentage:
              t.realisedPNLPercentage ??
              t.realisedPnLPercentage ??
              t.realizedPNLPercentage ??
              t.realizedPnLPercentage,
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    costValue: normalized.amount ?? normalized.costValue ?? normalized.investedAmount,
    currentValue: normalized.currentAmount ?? normalized.currentValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPerc ?? normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const schemeName = getHoldingSchemeName(row);
  const normalized = normalizeRealizedRow({ ...row, schemeName });
  const pnl =
    row.realisedPnl ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    row.realizedGainLoss ??
    normalized.realisedPnl ??
    normalized.gainLoss;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage ??
    normalized.realisedPNLPercentage ??
    normalized.gainLossPerc ??
    normalized.gainLossPercentage;
  return {
    ...normalized,
    schemeName,
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: row.currentAmount ?? row.currentValue,
    gainLoss: pnl ?? "-",
    gainLossPercentage: pnlPct ?? "-",
  };
};

const extractPrivateEquityHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null => {
  const info =
    payload?.totalHoldingInfo ??
    payload?.totalHolding ??
    payload?.data?.totalHoldingInfo ??
    null;
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    gainLossPercentage: info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagrPrivateStock: info.cagrPrivateStock ?? info.cagr,
    cagr: info.cagr ?? info.cagrPrivateStock,
  };
};

export type ParsedPrivateEquityReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parsePrivateEquityReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedPrivateEquityReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding": {
      const holdings = extractPrivateEquityHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractPrivateEquityHoldings(payload);
      rows = filterRealizedTxnRows(
        flattenPrivateEquityHoldingsTransactions(holdings),
      ).map(normalizeRealizedRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractPrivateEquityHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenPrivateEquityHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractPrivateEquityHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenPrivateEquityHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergePrivateEquityTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.symbol ?? r.schemeName}-${r.transactionDate}-${r.units}-${r.transType}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.symbol ?? row.schemeName}-${row.transactionDate}-${row.units}-${row.transType}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
